import React, { useState } from 'react';
import { App, Button, Card, Form, Input, Spin, Typography } from 'antd';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import cspmService from '@/services/cspm.service';

const { Title, Text, Paragraph } = Typography;

/**
 * Public customer acknowledgement for Customer Property Return (Phase 2).
 * Tokenized — no login.
 */
const CustomerPropertyReturnAckPublicPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();

  const { data, isLoading, error } = useQuery({
    queryKey: ['cspm', 'public-return-ack', token],
    queryFn: () => cspmService.getPublicReturnAck(token!),
    enabled: Boolean(token),
    retry: false,
  });

  const submit = useMutation({
    mutationFn: (values: { acknowledged_by_name: string; acknowledgement_notes?: string }) =>
      cspmService.postPublicReturnAck(token!, values),
    onSuccess: () => {
      message.success('Thank you — return acknowledged');
      queryClient.invalidateQueries({ queryKey: ['cspm', 'public-return-ack', token] });
    },
    onError: (e: unknown) => {
      message.error((e as Error)?.message || 'Could not submit acknowledgement');
    },
  });

  if (isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ maxWidth: 560, margin: '48px auto', padding: 24 }}>
        <Title level={3}>Link not found</Title>
        <Paragraph type="secondary">
          This acknowledgement link is invalid or has expired. Ask your supplier for a new link.
        </Paragraph>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 640, margin: '32px auto', padding: 24 }}>
      <Card>
        <Title level={3} style={{ marginTop: 0 }}>
          Externally owned property return acknowledgement
        </Title>
        <Text type="secondary">Return {data.return_number}</Text>

        <div style={{ marginTop: 16 }}>
          {(data.lines || []).map((ln, i) => (
            <div key={i} style={{ marginBottom: 8 }}>
              <Text strong>
                {ln.description} — {ln.quantity} {ln.uom}
              </Text>
              {ln.serial_number ? (
                <div>
                  <Text type="secondary">S/N {ln.serial_number}</Text>
                </div>
              ) : null}
            </div>
          ))}
        </div>

        {data.already_acknowledged ? (
          <Paragraph style={{ marginTop: 24 }}>
            Already acknowledged by <Text strong>{data.customer_ack_name || '—'}</Text>
            {data.customer_ack_at ? ` on ${data.customer_ack_at}` : ''}.
          </Paragraph>
        ) : (
          <Form
            form={form}
            layout="vertical"
            style={{ marginTop: 24 }}
            onFinish={(v) => submit.mutate(v)}
          >
            <Form.Item
              name="acknowledged_by_name"
              label="Your name"
              rules={[{ required: true, message: 'Name is required' }]}
            >
              <Input size="large" placeholder="Customer representative name" />
            </Form.Item>
            <Form.Item name="acknowledgement_notes" label="Notes (optional)">
              <Input.TextArea rows={3} />
            </Form.Item>
            <Button type="primary" htmlType="submit" size="large" loading={submit.isPending} block>
              Confirm receipt of returned property
            </Button>
          </Form>
        )}
      </Card>
    </div>
  );
};

export default CustomerPropertyReturnAckPublicPage;
